import { User } from 'firebase/auth'
import { roundMoney, sumMoney } from '@/lib/money'
import { Earning } from '@/types/earnings'
import { Compensation } from '@/types/compensation'
import { DriverMaster } from '@/types/driverMaster'
import {
  CompleteSalarySnapshot,
  SalaryDriverInfo,
  SalaryRawEarning,
  SalaryDriverTaxSettings,
  SalaryAggregatedTotals,
  convertDriverToInfo,
  convertEarningToRaw
} from '@/types/salarySnapshot'
import { SalaryCalculationService, SalaryCalculationConfig, AggregatedEarningsInput } from './salaryCalculationService'
import { EarningsService } from './earningsService'
import { CompensationService } from './compensationService'
import { DriverMasterService } from './driverMasterService'
import { PeriodService } from './periodService'
import { TaxTableService } from './taxTableService'
import { useConfigStore } from '@/stores/configStore'

/**
 * Service for creating unified salary snapshots
 *
 * This is the single source of truth for salary snapshot creation.
 * All salary calculations (preview or saved) should go through this service
 * to ensure consistency and eliminate discrepancies.
 *
 * Key features:
 * - Creates snapshots from earnings, compensations, and settings
 * - Uses SalaryCalculationService as the single calculation engine
 * - Aggregates results into a complete snapshot
 * - Supports both preview mode (in-memory) and save mode (for Firestore)
 */
export class SalarySnapshotService {
  /**
   * Get fleet settings in the format expected by SalaryCalculationService
   * Converts percentage values from store (0-100) to decimals (0-1)
   */
  private static getFleetSettings(): SalaryCalculationConfig {
    const settings = useConfigStore.getState().settings

    return {
      driverRevenueShare: settings.driverRevenueShare / 100,  // 40 → 0.40
      vacationPayRate: settings.vacationPayRate / 100,        // 12.5 → 0.125
      vatRate: settings.vatRate / 100,                        // 6 → 0.06
      taxRate: settings.taxRate / 100,                        // 30 → 0.30
      feeOnTips: settings.feeOnTips || 'fleet_commission',
      taxCalculationMethod: settings.taxCalculationMethod || 'fixed',
      taxTableName: settings.taxTableName || 'skattetabell31',
      taxColumn: settings.taxColumn ?? 1,
      employerTaxRate: (settings.employerTaxRate || 31.42) / 100, // 31.42 → 0.3142
      revenueShareMode: settings.revenueShareMode ?? 'single',
      revenueShareTiers: settings.revenueShareTiers,
    }
  }

  /**
   * Extract driver-specific tax settings if they exist
   *
   * Note: salaryGarnishment is tracked here for future use but not yet
   * included in salary calculations. It will be a post-tax deduction
   * when implemented.
   */
  private static extractDriverTaxSettings(driver: DriverMaster): SalaryDriverTaxSettings | undefined {
    const hasDriverSpecificTax = (
      driver.taxCalculationMethod ||
      driver.taxRate !== undefined ||
      driver.taxTableName ||
      driver.taxColumn !== undefined ||
      driver.salaryGarnishment !== undefined
    )

    if (!hasDriverSpecificTax) {
      return undefined
    }

    return {
      ...(driver.taxCalculationMethod && { taxCalculationMethod: driver.taxCalculationMethod }),
      ...(driver.taxRate !== undefined && { taxRate: driver.taxRate }),
      ...(driver.taxTableName && { taxTableName: driver.taxTableName }),
      ...(driver.taxColumn !== undefined && { taxColumn: driver.taxColumn }),
      ...(driver.salaryGarnishment !== undefined && { salaryGarnishment: driver.salaryGarnishment }),
    }
  }

  /**
   * Merge fleet settings with driver-specific settings
   * Driver settings override fleet settings when present
   */
  static mergeSettings(
    fleetSettings: SalaryCalculationConfig,
    driverTaxSettings?: SalaryDriverTaxSettings
  ): SalaryCalculationConfig {
    if (!driverTaxSettings) {
      return fleetSettings
    }

    return {
      ...fleetSettings,
      ...(driverTaxSettings.taxCalculationMethod && {
        taxCalculationMethod: driverTaxSettings.taxCalculationMethod
      }),
      ...(driverTaxSettings.taxRate !== undefined && {
        taxRate: driverTaxSettings.taxRate / 100 // Convert percentage to decimal
      }),
      ...(driverTaxSettings.taxTableName && {
        taxTableName: driverTaxSettings.taxTableName.replace(/\D/g, '') // Strip "skattetabell" prefix
      }),
      ...(driverTaxSettings.taxColumn !== undefined && {
        taxColumn: driverTaxSettings.taxColumn
      }),
    }
  }

  /**
   * Aggregate raw earnings into input for calculateAggregatedSalary
   */
  private static aggregateRawEarnings(earnings: Earning[]): AggregatedEarningsInput {
    return {
      grossEarnings: sumMoney(earnings.map(e => e.grossEarnings)),
      netEarnings: sumMoney(earnings.map(e => e.netEarnings)),
      tips: sumMoney(earnings.map(e => e.tips || 0)),
      cashReceived: sumMoney(earnings.map(e => e.cashReceived || 0))
    }
  }

  /**
   * Create a complete salary snapshot for a driver
   *
   * IMPORTANT: This method now uses aggregate-first tax calculation.
   * Tax is calculated ONCE on the aggregated gross salary, which is correct
   * for Swedish progressive tax tables when a driver has multiple earnings.
   *
   * This method is used for both:
   * - Preview calculations (pass earnings/compensations directly)
   * - Firestore persistence (will be called by LockedSalaryDataService)
   *
   * @param driverId - Driver ID
   * @param period - Period string (yyyy-mm-01)
   * @param earnings - Driver's earnings for the period
   * @param compensations - Driver's compensations for the period
   * @param driverInfo - Driver information
   * @param driverTaxSettings - Optional driver-specific tax settings
   * @returns Complete salary snapshot with all calculations
   */
  static createSnapshot(
    driverId: string,
    period: string,
    earnings: Earning[],
    compensations: Compensation[],
    driverInfo: SalaryDriverInfo,
    driverTaxSettings?: SalaryDriverTaxSettings,
    taxYear?: number
  ): CompleteSalarySnapshot {
    // Validate inputs
    if (!driverId || driverId.trim().length === 0) {
      throw new Error('driverId är obligatorisk')
    }
    if (!period || period.trim().length === 0) {
      throw new Error('period är obligatorisk')
    }
    if (earnings.length === 0 && compensations.length === 0) {
      throw new Error('Inga intäkter hittades för snapshot-skapande')
    }
    // Get fleet settings
    const fleetSettings = this.getFleetSettings()

    // Merge with driver-specific settings, then inject taxYear
    const mergedSettings = this.mergeSettings(fleetSettings, driverTaxSettings)
    const effectiveSettings: SalaryCalculationConfig = taxYear !== undefined
      ? { ...mergedSettings, taxYear }
      : mergedSettings

    // Convert earnings to raw format (for per-platform breakdown)
    const rawEarnings: SalaryRawEarning[] = earnings.map(convertEarningToRaw)

    // Step 1: Aggregate raw earnings
    const aggregatedInput = this.aggregateRawEarnings(earnings)

    // Step 2: Calculate salary with tax on aggregated amount (correct for progressive tax)
    const aggregatedResult = SalaryCalculationService.calculateAggregatedSalary(
      aggregatedInput,
      effectiveSettings,
      compensations
    )

    // Step 3: Convert aggregated result to SalaryAggregatedTotals
    const totals: SalaryAggregatedTotals = {
      grossEarnings: aggregatedResult.grossEarnings,
      netEarnings: aggregatedResult.netEarnings,
      tips: aggregatedResult.tips,
      cashReceived: aggregatedResult.cashReceived,
      vatAmount: aggregatedResult.vatAmount,
      shareableAmount: aggregatedResult.shareableAmount,
      afterFleetCommission: aggregatedResult.afterFleetCommission,
      vacationPay: aggregatedResult.vacationPay,
      appliedDriverRevenueShare: aggregatedResult.appliedDriverRevenueShare,
      grossSalary: aggregatedResult.grossSalary,
      finalGrossSalary: aggregatedResult.finalGrossSalary,
      employerTaxAmount: aggregatedResult.employerTaxAmount,
      totalEmployerCost: aggregatedResult.totalEmployerCost,
      netSalary: aggregatedResult.netSalary,
      finalNetSalary: aggregatedResult.finalNetSalary,
      taxAmount: aggregatedResult.taxAmount,
      effectiveTaxRate: aggregatedResult.effectiveTaxRate,
      untaxedTips: aggregatedResult.untaxedTips,
      amountToPay: aggregatedResult.amountToPay,
      netEarningsCompensations: aggregatedResult.netEarningsCompensations,
      netEarningsDeductions: aggregatedResult.netEarningsDeductions,
      grossSalaryCompensations: aggregatedResult.grossSalaryCompensations,
      grossSalaryDeductions: aggregatedResult.grossSalaryDeductions,
      netSalaryCompensations: aggregatedResult.netSalaryCompensations,
      netSalaryDeductions: aggregatedResult.netSalaryDeductions
    }

    // Build complete snapshot
    // Note: calculatedEarnings is now empty - all calculations are in totals
    return {
      driverId,
      period,
      driverInfo,
      rawEarnings,
      calculatedEarnings: [], // No longer needed - use totals instead
      compensations,
      fleetSettings,
      driverTaxSettings,
      totals
    }
  }

  /**
   * Create snapshot from database for a specific driver and period
   *
   * This is a convenience method that fetches data from the database
   * and creates a snapshot. Useful for generating previews.
   *
   * @param user - Authenticated user
   * @param driverId - Driver ID
   * @param period - Period string (yyyy-mm-01)
   * @returns Complete salary snapshot
   */
  static async createSnapshotFromDatabase(
    user: User,
    driverId: string,
    period: string
  ): Promise<CompleteSalarySnapshot> {
    try {
      // Fetch driver, earnings, compensations, and period in parallel
      const [driver, allEarnings, compensations, periodDoc] = await Promise.all([
        DriverMasterService.getDriver(user, driverId),
        EarningsService.getEarningsByPeriod(user, period),
        CompensationService.getCompensationsByDriverAndPeriod(user, driverId, period),
        PeriodService.getPeriod(user, period)
      ])

      if (!driver) {
        throw new Error('Föraren hittades inte')
      }

      const driverEarnings = allEarnings.filter(
        e => e.driverId === driverId && !e.isDeleted
      )

      const activeCompensations = compensations.filter(c => !c.isDeleted)

      if (driverEarnings.length === 0 && activeCompensations.length === 0) {
        throw new Error('Inga intäkter hittades för denna förare i denna period')
      }

      // Resolve tax year from payout date (if set) or period year
      const payoutDate = periodDoc?.payoutDate?.toDate()
      const taxYear = TaxTableService.getTaxYear(period, payoutDate)

      // Convert driver to info and extract driver-specific tax settings
      const driverInfo = convertDriverToInfo(driver)
      const driverTaxSettings = this.extractDriverTaxSettings(driver)

      // Ensure the correct year's tax table is loaded before calculating
      const mergedSettings = this.mergeSettings(this.getFleetSettings(), driverTaxSettings)
      if (mergedSettings.taxCalculationMethod === 'table') {
        const tableNumber = parseInt(mergedSettings.taxTableName?.replace(/\D/g, '') || '31')
        await TaxTableService.loadTaxTable(tableNumber, taxYear)
      }

      // Create and return snapshot with resolved tax year
      return this.createSnapshot(
        driverId,
        period,
        driverEarnings,
        activeCompensations,
        driverInfo,
        driverTaxSettings,
        taxYear
      )
    } catch (error) {
      console.error('Error creating snapshot from database:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att skapa löne-snapshot')
    }
  }

  /**
   * Create snapshots for multiple drivers in a period
   *
   * Useful for bulk operations like generating salary reports
   *
   * @param user - Authenticated user
   * @param period - Period string (yyyy-mm-01)
   * @param driverIds - Optional list of driver IDs (if not provided, creates for all drivers with earnings)
   * @returns Array of complete salary snapshots
   */
  static async createSnapshotsForPeriod(
    user: User,
    period: string,
    driverIds?: string[]
  ): Promise<CompleteSalarySnapshot[]> {
    try {
      // Fetch all earnings and compensations for the period
      const [allEarnings, allCompensations] = await Promise.all([
        EarningsService.getEarningsByPeriod(user, period),
        CompensationService.getCompensationsByPeriod(user, period)
      ])

      // Get unique driver IDs from both earnings and compensations (or use provided list)
      const targetDriverIds = driverIds || [
        ...new Set([
          ...allEarnings.map(e => e.driverId),
          ...allCompensations.map(c => c.driverId)
        ].filter(Boolean))
      ] as string[]

      if (targetDriverIds.length === 0) {
        return []
      }

      // Create snapshots in parallel
      const snapshots = await Promise.all(
        targetDriverIds.map(driverId =>
          this.createSnapshotFromDatabase(user, driverId, period)
            .catch(err => {
              console.error(`Failed to create snapshot for driver ${driverId}:`, err)
              return null
            })
        )
      )

      // Filter out failed snapshots
      return snapshots.filter((s): s is CompleteSalarySnapshot => s !== null)
    } catch (error) {
      console.error('Error creating snapshots for period:', error)
      throw new Error('Det gick inte att skapa löne-snapshots för perioden')
    }
  }
}
